import {createContext,useContext,type ReactNode} from 'react';
import type {Settings} from '../types';
import {loadSettings} from './storage';
import {shopRows} from './shopTranslations';
export type ShopLanguage=Settings['language'];
export type ShopTranslations=Partial<Record<ShopLanguage,{name?:string;description?:string;specifications?:{name:string;value:string}[];taxNote?:string;selectionLabel?:string;optionLabels?:Record<string,string>}>>;
const indices={pt:-1,de:0,en:1,fr:2,it:3};
const locales={pt:'pt-PT',de:'de-CH',en:'en-CH',fr:'fr-CH',it:'it-CH'};
const ShopLanguageContext=createContext<ShopLanguage|undefined>(undefined);
export function ShopLocale({language,children}:{language?:ShopLanguage;children:ReactNode}){
 const inherited=useContext(ShopLanguageContext);
 const selected=language||inherited||loadSettings().language;
 const safe=Object.hasOwn(indices,selected)?selected:'pt';
 return <ShopLanguageContext.Provider value={safe}><div data-native-i18n lang={safe} style={{display:'contents'}}>{children}</div></ShopLanguageContext.Provider>;
}
export function translateShop(source:string,language:ShopLanguage):string {
 if(language==='pt')return source;
 const row=shopRows[source];if(row)return row[indices[language]];
 // Preserve the reference of messages generated after a successful order.
 for(const prefix of ['Pedido enviado à equipa. Referência ','Compra simulada concluída: ']){
   if(source.startsWith(prefix))return shopRows[prefix][indices[language]]+source.slice(prefix.length);
 }
 return source;
}
export function localizeShopRecord<T extends {name?:string;description?:string;specifications?:{name:string;value:string}[];taxNote?:string;selectionLabel?:string;optionLabels?:Record<string,string>;translations?:ShopTranslations}>(record:T,language:ShopLanguage):T {
 const translated=record.translations?.[language];
 const result={...record};
 for(const key of ['name','description','taxNote','selectionLabel'] as const){
   if(typeof record[key]==='string') (result as Record<string,unknown>)[key]=translated?.[key]?.trim()?translated[key]:translateShop(record[key]!,language);
 }
 if(record.specifications)result.specifications=record.specifications.map((spec,i)=>({
   name:translated?.specifications?.[i]?.name?.trim()||translateShop(spec.name,language),
   value:translated?.specifications?.[i]?.value?.trim()||translateShop(spec.value,language)
 }));
 return result;
}
export function useShopI18n(){
 const language=useContext(ShopLanguageContext)||loadSettings().language;
 return {language,tr:(value:string)=>translateShop(value,language),
 money:(c:number|null)=>c===null?translateShop('Sob orçamento',language):new Intl.NumberFormat(locales[language],{style:'currency',currency:'CHF'}).format(c/100),
 localize:<T extends Parameters<typeof localizeShopRecord>[0]>(record:T)=>localizeShopRecord(record,language)};
}
